<template>
  <div>
    <div class="q-mb-md">Bitte begleichen Sie Ihre offenen Rechnungen</div>

    <q-alert
      v-if="overdue"
      class="open-invoices-alert q-mb-lg"
      color="negative"
      icon="error"
      message="Zugriff auf die Anwendung ist derzeit blockiert."
      detail="Für Ihr Abonnement bestehen noch offene Rechnungen. Bitte begleichen Sie die offenen Beträge, um die Nutzung von Autengo wieder freizuschalten. Nach erfolgreicher Zahlung werden Sie automatisch wieder Zugriff auf die Anwendung erhalten."
    />
    <q-alert
      v-else-if="paymentDeadline"
      class="open-invoices-alert q-mb-lg"
      color="warning"
      icon="warning"
      :message="`Bitte begleichen Sie die offenen Rechnungen bis ${deadlineText}.`"
      detail="Sollten die Rechnungen bis dahin nicht beglichen sein, wird der Zugang zu Autengo gesperrt. Nach erfolgreicher Zahlung werden Sie automatisch wieder Zugriff auf die Anwendung erhalten."
    />

    <div v-if="loading" class="row justify-center q-pa-lg">
      <q-spinner color="primary" :size="30" />
    </div>

    <div v-else-if="invoices.length < 1" class="row justify-center text-faded q-pa-lg">
      Keine offenen Rechnungen vorhanden.
    </div>

    <q-card v-else flat>
      <q-table
        :data="invoices"
        :columns="columns"
        row-key="id"
        hide-bottom
        class="no-shadow"
        :pagination="{ rowsPerPage: 1000, page: 1 }"
      >
        <template slot="body" slot-scope="props">
          <q-tr :props="props">
            <q-td key="created" :props="props">{{ props.row.created | formatUnixDate }}</q-td>
            <q-td key="number" :props="props">{{ props.row.number }}</q-td>
            <q-td key="amount_remaining" :props="props">
              {{ props.row.amount_remaining | currencyCentsToEuro }}
            </q-td>
            <q-td key="status" :props="props">
              <q-chip v-if="isProcessing(props.row)" dense color="grey">
                In Bearbeitung
              </q-chip>
              <q-chip v-else dense color="negative">Offen</q-chip>
            </q-td>
            <q-td key="actions" :props="props" class="no-wrap">
              <q-btn
                class="no-shadow q-mr-sm"
                icon="visibility"
                label="Rechnung ansehen"
                :disable="!props.row.invoice_pdf"
                @click="openUrl(props.row.invoice_pdf)"
              />
              <q-btn
                class="no-shadow"
                :color="isProcessing(props.row) ? 'grey' : 'primary'"
                icon="credit_card"
                label="Jetzt bezahlen"
                :disable="!props.row.hosted_invoice_url || isProcessing(props.row)"
                @click="openUrl(props.row.hosted_invoice_url)"
              />
            </q-td>
          </q-tr>
        </template>
      </q-table>

      <div class="column items-end q-pa-md">
        <div class="text-faded q-body-1">Offener Gesamtbetrag</div>
        <div class="q-headline text-weight-bold">{{ totalAmount | currencyCentsToEuro }}</div>
      </div>
    </q-card>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';

export default {
  name: 'OpenInvoicesTable',
  props: {
    // Access is already blocked due to the overdue payment
    overdue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      invoices: [],
      loading: true,
      columns: [
        {
          name: 'created', label: 'Erstellt', field: 'created', align: 'left',
        },
        {
          name: 'number', label: 'Rechnungsnummer', field: 'number', align: 'left',
        },
        {
          name: 'amount_remaining', label: 'Betrag', field: 'amount_remaining', align: 'right',
        },
        {
          name: 'status', label: 'Status', field: 'status', align: 'left',
        },
        {
          name: 'actions', label: '', field: 'actions', align: 'right',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('subscription', ['paymentDeadline']),
    deadlineText() {
      return moment(this.paymentDeadline).format('DD.MM.YYYY HH:mm');
    },
    totalAmount() {
      return this.invoices.reduce((sum, invoice) => sum + invoice.amount_remaining, 0);
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      try {
        this.invoices = await this.$store.dispatch('subscription/getOpenInvoices');

        // Everything is paid -> refetch the subscriptions, so the payment hint disappears
        if (this.invoices.length < 1) {
          await this.$store.dispatch('subscription/getSubscriptions', true);
        }
      } finally {
        this.loading = false;
      }
    },
    // Payment is already processing (e.g. SEPA), so it can't be paid again
    isProcessing(invoice) {
      return invoice.latest_charge === 'pending';
    },
    openUrl(url) {
      window.open(url);
    },
  },
};
</script>

<style scoped>
.footer-note {
  max-width: 300px;
  text-align: right;
}

.open-invoices-alert :deep(.q-alert) {
  border-radius: 4px;
  border-left-width: 4px !important;
  box-shadow: none;
}

.open-invoices-alert :deep(.q-alert-side) {
  align-self: flex-start;
  background: transparent;
  font-size: 20px;
  padding: 12px 0 12px 16px;
}

.open-invoices-alert :deep(.q-alert-content) {
  font-size: 14px;
}

.open-invoices-alert :deep(.q-alert-content > div:first-child) {
  font-weight: 500;
}

.open-invoices-alert :deep(.q-alert-detail) {
  color: #475467;
  opacity: 1;
  font-size: 14px;
  margin-top: 8px;
}
</style>
